import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, Calendar, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AgendaTimeline, formatEventDate } from '@/components/EventDetailParts';
import MemberProfileModal from '@/components/MemberProfileModal';
import { stableAgendaItemId, computeClashes } from '@/lib/agendaUtils';

const BACKEND_URL = '';

/**
 * EventAgendaPage — dedicated route /events/:eventId/agenda.
 * Hosts location filter, My Agenda starring, clash warnings, and
 * SMS reminder opt-in (Bird.com 10-min before session).
 */
export default function EventAgendaPage({ user: userProp }) {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myPicks, setMyPicks] = useState(new Set());
  const [user, setUser] = useState(userProp || null);
  const [viewingMember, setViewingMember] = useState(null);
  const [smsStatus, setSmsStatus] = useState({ opted_in: false, phone: null });
  const [reminderChannel, setReminderChannel] = useState('email');
  const [showSmsForm, setShowSmsForm] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [smsSaving, setSmsSaving] = useState(false);

  // BriteLab registration state
  const [britelabCounts, setBritelabCounts] = useState({});
  const [myBritelabRegs, setMyBritelabRegs] = useState(new Set());
  const [eventRsvpd, setEventRsvpd] = useState(false);
  const [clashDialog, setClashDialog] = useState(null);  // {item, clashItem}

  // Hydrate user (same pattern as EventDetailPage)
  useEffect(() => {
    if (userProp) { setUser(userProp); return; }
    const cached = sessionStorage.getItem('sll_user') || localStorage.getItem('sll_user');
    if (cached) { try { setUser(JSON.parse(cached)); } catch {} }
    fetch(`${BACKEND_URL}/api/auth/session`, { credentials: 'include' })
      .then(r => {
        if (r.status === 401) {
          ['sll_user', 'session_token'].forEach(k => sessionStorage.removeItem(k));
          ['sll_user', 'sll_session_token'].forEach(k => localStorage.removeItem(k));
          setUser(null);
        }
        return r.ok ? r.json() : null;
      })
      .then(fresh => { if (fresh) setUser(fresh); })
      .catch(() => {});
  }, [userProp]);

  // Load event
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/events`, { credentials: 'include' });
        const list = res.ok ? await res.json() : [];
        const ev = Array.isArray(list) ? list.find(e => e.event_id === eventId) : null;
        if (active) { setEvent(ev || null); setLoading(false); }
      } catch { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [eventId]);

  // Load admin reminder channel setting (public)
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/settings/reminders`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.channel) setReminderChannel(d.channel); })
      .catch(() => {});
  }, []);

  // Load My Agenda picks + SMS status (requires login)
  useEffect(() => {
    if (!user?.user_id || !eventId) return;
    fetch(`${BACKEND_URL}/api/events/${eventId}/my-agenda`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.picks) setMyPicks(new Set(d.picks)); })
      .catch(() => {});
    fetch(`${BACKEND_URL}/api/me/sms-opt-in`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setSmsStatus(d); setSmsPhone(d.phone || ''); } })
      .catch(() => {});
  }, [user, eventId]);

  // Load BriteLab counts (public) + my registrations
  const loadBritelabCounts = () => {
    fetch(`${BACKEND_URL}/api/events/${eventId}/britelab/counts`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setBritelabCounts(d.counts || {});
          setMyBritelabRegs(new Set(d.my_registrations || []));
          setEventRsvpd(!!d.event_rsvpd);
        }
      }).catch(() => {});
  };
  useEffect(() => {
    if (!eventId) return;
    loadBritelabCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, user]);

  const hasBritelabs = Object.values(britelabCounts).some(c => c?.is_britelab);

  // Clashes computed from starred items
  const starredItems = useMemo(() => {
    if (!event?.agenda) return [];
    return event.agenda.filter(it => myPicks.has(stableAgendaItemId(it)));
  }, [event, myPicks]);

  const clashes = useMemo(() => computeClashes(starredItems), [starredItems]);

  const toggleStar = async (itemId, isStarring) => {
    if (!user?.user_id) {
      toast.error('Please sign in to star sessions');
      navigate('/login?next=' + encodeURIComponent(`/events/${eventId}/agenda`));
      return;
    }
    const next = new Set(myPicks);
    if (isStarring) next.add(itemId); else next.delete(itemId);
    setMyPicks(next); // optimistic
    try {
      if (isStarring) {
        await fetch(`${BACKEND_URL}/api/events/${eventId}/my-agenda`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ item_id: itemId }),
        });
      } else {
        await fetch(`${BACKEND_URL}/api/events/${eventId}/my-agenda/${itemId}`, {
          method: 'DELETE', credentials: 'include',
        });
      }
    } catch (e) {
      toast.error('Could not update My Agenda');
      // revert
      const revert = new Set(myPicks);
      setMyPicks(revert);
    }
  };

  // BriteLab register / unregister
  const toggleRegister = async (item, isRegistering, { swap = false } = {}) => {
    if (!user?.user_id) {
      toast.error('Please sign in to register');
      navigate('/login?next=' + encodeURIComponent(`/events/${eventId}/agenda`));
      return;
    }
    if (!eventRsvpd && isRegistering) {
      toast.error('Register for the event first', {
        action: { label: 'Go to event', onClick: () => navigate(`/events/${eventId}`) },
      });
      return;
    }
    const itemId = stableAgendaItemId(item);
    try {
      if (isRegistering) {
        const url = `${BACKEND_URL}/api/events/${eventId}/britelab/${itemId}/register${swap ? '?swap=true' : ''}`;
        const r = await fetch(url, { method: 'POST', credentials: 'include' });
        if (r.status === 409) {
          const err = await r.json();
          if (err?.detail?.error === 'clash') {
            setClashDialog({ item, clashTitle: err.detail.clash_title });
            return;
          }
          toast.error(err?.detail || 'Could not register');
          return;
        }
        if (!r.ok) throw new Error('register failed');
        toast.success('Registered — added to My Agenda');
      } else {
        await fetch(`${BACKEND_URL}/api/events/${eventId}/britelab/${itemId}/register`, {
          method: 'DELETE', credentials: 'include',
        });
        toast.success('Unregistered');
      }
      loadBritelabCounts();
      // refresh picks since register auto-stars / unregister may auto-unstar
      fetch(`${BACKEND_URL}/api/events/${eventId}/my-agenda`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.picks) setMyPicks(new Set(d.picks)); });
    } catch (e) {
      toast.error('Something went wrong');
    }
  };

  const confirmSwap = async () => {
    if (!clashDialog) return;
    const { item } = clashDialog;
    setClashDialog(null);
    await toggleRegister(item, true, { swap: true });
  };

  const saveSmsOptIn = async () => {
    if (!smsConsent) { toast.error('Please tick the consent box'); return; }
    setSmsSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/me/sms-opt-in`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: smsPhone.trim(),
          consent_text: 'I agree to receive 10-minute pre-session SMS reminders from BriteHub for sessions I have starred.',
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      setSmsStatus({ opted_in: true, phone: d.phone });
      setShowSmsForm(false);
      toast.success('SMS reminders enabled');
    } catch (e) {
      toast.error(e.message || 'Could not opt in');
    } finally {
      setSmsSaving(false);
    }
  };

  const optOutSms = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/me/sms-opt-in`, { method: 'DELETE', credentials: 'include' });
      setSmsStatus({ opted_in: false, phone: null });
      setSmsPhone('');
      toast.success('SMS reminders turned off');
    } catch { toast.error('Could not opt out'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <div className="max-w-5xl mx-auto px-4 py-8 text-sm text-slate-500">Loading agenda…</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <div className="max-w-5xl mx-auto px-4 py-8">
          <p className="text-sm text-slate-500">Event not found.</p>
          <Button onClick={() => navigate('/events')} className="mt-4" data-testid="back-to-events-btn">Back to Events</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />

      <div className="max-w-5xl mx-auto px-4 py-6 md:py-10">
        <button
          type="button"
          onClick={() => navigate(`/events/${event.event_id}`)}
          data-testid="back-to-event-btn"
          className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-brand-navy mb-4">
          <ArrowLeft className="w-3 h-3" /> Back to event
        </button>

        <header className="mb-6">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-navy" data-testid="agenda-page-title">
            {event.title}
          </h1>
          <p className="text-sm text-slate-500 mt-1 inline-flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{formatEventDate(event.date)}</span>
            {event.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</span>}
          </p>
        </header>

        {/* Reminders card — content varies by admin channel setting */}
        {user?.user_id && (
          <Card className="p-4 md:p-5 mb-6 bg-white border-brand-navy/10" data-testid="reminder-card">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-100 rounded-full">
                <Bell className="w-4 h-4 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-slate-800">10-minute reminders</h3>

                {/* EMAIL (default) — automatic, no opt-in needed */}
                {reminderChannel === 'email' && (
                  <p className="text-xs text-slate-500 mt-1" data-testid="reminder-email-info">
                    You'll get an email 10 minutes before each session you star — sent to <strong>{user.email}</strong>. No opt-in needed.
                  </p>
                )}

                {/* SMS or BOTH — show opt-in form */}
                {(reminderChannel === 'sms' || reminderChannel === 'both') && (
                  <>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {reminderChannel === 'both'
                        ? <>You'll always get an email reminder 10 min before each starred session. Enable SMS below for an extra text message. UK mobile only.</>
                        : <>Get a text message 10 minutes before each session you've starred. UK mobile numbers only.</>}
                    </p>

                    {smsStatus.opted_in ? (
                      <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                        <p className="text-xs font-medium text-green-700" data-testid="sms-optin-status-on">
                          ✓ SMS enabled — texts will go to {smsStatus.phone}
                        </p>
                        <Button variant="outline" size="sm" onClick={optOutSms} data-testid="sms-optout-btn">
                          Turn off SMS
                        </Button>
                      </div>
                    ) : !showSmsForm ? (
                      <Button
                        size="sm"
                        className="mt-3 bg-amber-500 hover:bg-amber-600"
                        onClick={() => setShowSmsForm(true)}
                        data-testid="sms-enable-btn">
                        <Phone className="w-3 h-3 mr-1" /> Enable SMS reminders
                      </Button>
                    ) : (
                      <div className="mt-3 space-y-3">
                        <div>
                          <Label htmlFor="sms-phone" className="text-xs">UK mobile (E.164 format)</Label>
                          <Input
                            id="sms-phone"
                            type="tel"
                            placeholder="+447700900123"
                            value={smsPhone}
                            onChange={e => setSmsPhone(e.target.value)}
                            data-testid="sms-phone-input"
                            className="mt-1" />
                        </div>
                        <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                          <Checkbox
                            checked={smsConsent}
                            onCheckedChange={v => setSmsConsent(v === true)}
                            data-testid="sms-consent-checkbox" />
                          <span>
                            I agree to receive 10-minute pre-session SMS reminders from BriteHub for sessions I have starred.
                            Reply STOP in any text to unsubscribe. Standard carrier charges may apply.
                          </span>
                        </label>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={saveSmsOptIn} disabled={smsSaving || !smsConsent}
                            data-testid="sms-save-btn">
                            {smsSaving ? 'Saving…' : 'Opt in'}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setShowSmsForm(false)}
                            data-testid="sms-cancel-btn">
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </Card>
        )}

        {hasBritelabs && !eventRsvpd && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="britelab-booking-notice">
            <p className="text-sm text-amber-900 flex-1">
              {user?.user_id
                ? <>To book BriteLab sessions you first need a place at <strong>{event.title}</strong>.</>
                : <>Sign in to book BriteLab sessions. You'll need a place at <strong>{event.title}</strong>.</>}
            </p>
            <Button size="sm" className="bg-brand-navy hover:bg-brand-dark text-white"
              data-testid="britelab-booking-notice-btn"
              onClick={() => navigate(user?.user_id ? `/events/${eventId}` : '/login?next=' + encodeURIComponent(`/events/${eventId}/agenda`))}>
              {user?.user_id ? 'Register for the event' : 'Sign in'}
            </Button>
          </div>
        )}

        {/* Agenda */}
        <Card className="p-4 md:p-6 bg-white">
          <AgendaTimeline
            agenda={event.agenda || []}
            locations={event.locations || []}
            myPicks={myPicks}
            clashes={clashes}
            canStar={!!user?.user_id}
            onToggleStar={toggleStar}
            onViewSpeaker={(spk) => setViewingMember(spk)}
            britelabCounts={britelabCounts}
            myBritelabRegs={myBritelabRegs}
            eventRsvpd={eventRsvpd}
            onToggleRegister={toggleRegister}
          />
        </Card>
      </div>

      {/* Clash swap dialog */}
      <Dialog open={!!clashDialog} onOpenChange={(o) => { if (!o) setClashDialog(null); }}>
        <DialogContent data-testid="britelab-clash-dialog">
          <DialogHeader>
            <DialogTitle>You're already registered for another BriteLab at this time</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              You're already registered for <strong>{clashDialog?.clashTitle}</strong> during this time slot.
              Register for this one instead and leave the other?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setClashDialog(null)} data-testid="clash-cancel-btn">
              Keep current
            </Button>
            <Button onClick={confirmSwap} data-testid="clash-swap-btn">
              Swap to this one
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {viewingMember && (
        <MemberProfileModal
          member={viewingMember}
          open={!!viewingMember}
          onClose={() => setViewingMember(null)}
          user={user} />
      )}
    </div>
  );
}
